import moment from 'moment';
import {
  LocationEntity,
  EntranceEntity,
  TimeslotEntity,
  ReservationEntity,
  WebviewAppData,
  WebviewQueryStringParameters,
  OccupantTypeEntity,
  GetLocationAppDataOptions,
  GetAllEntitiesForLocationOptions,
  GetAllEntitiesForCustomerOptions,
  GetExternalUserReservationsOptions,
  GetLocationOptions,
  GetEntranceOptions,
  GetTimeslotOptions,
  GetOccupantTypeOptions,
  GetReservationOptions,
  CurrentCheckIn,
  GetVisitTypeOptions,
  VisitTypeEntity,
} from '../../../../types';
import { logError, apiGetRequest } from '../../helpers';

const apiBaseURL = process.env.VUE_APP_API_URL || '';

const appData: WebviewAppData = {
  queryParameters: {
    externalUserId: null,
    webview: false,
    apiKey: null,
    customerId: null,
    locationId: null,
    occupantTypeId: null,
    visitTypeId: null,
    fcmToken: null,
    name: null,
    email: null,
    userLatitude: null,
    userLongitude: null,
    lang: 'en',
    widgetLayout: null,
    qrCode: null,
    cssURL: null,
    primaryColor: '#00B5EF',
    primaryFontColor: '#00B5EF',
    primaryButtonFontColor: '#FFF',
    secondColor: '#00B5EF',
    secondFontColor: '#00B5EF',
    secondButtonFontColor: '#FFF',
    thirdColor: '#00B5EF',
    thirdFontColor: '#00B5EF',
    thirdButtonFontColor: '#FFF',
    fourthColor: '#00B5EF',
    fourthFontColor: '#00B5EF',
    fourthButtonFontColor: '#FFF',
    headerBackgroundColor: '#1E1B1D',
    languagePickerColor: '#FFF',
  },
  locations: [],
  entrances: [],
  timeslots: [],
  occupantTypes: [],
  visitTypes: [],
  reservation: null,
  externalUser: {
    reservations: [],
    name: null,
    email: null,
    phone: null,
    occupantTypeId: null,
    apiKeyUsed: null,
  },
  currentCheckIn: {
    scan: null,
    location: null,
    reservation: null,
    externalUserId: null,
    autoCheckOutTimestamp: null,
  },
  loading: false,
};

export default {
  namespaced: true,
  state: appData,
  getters: {
    QUERY_PARAMETERS: (state: WebviewAppData) => state.queryParameters,
    LOCATIONS: (state: WebviewAppData) => state.locations,
    ENTRANCES: (state: WebviewAppData) => state.entrances,
    TIMESLOTS: (state: WebviewAppData) => state.timeslots,
    OCCUPANT_TYPES: (state: WebviewAppData) => state.occupantTypes,
    VISIT_TYPES: (state: WebviewAppData) => state.visitTypes,
    RESERVATION: (state: WebviewAppData) => state.reservation,
    EXTERNAL_USER_RESERVATIONS: (state: WebviewAppData) => state.externalUser.reservations,
    EXTERNAL_USER_NAME: (state: WebviewAppData) => state.externalUser.name,
    EXTERNAL_USER_EMAIL: (state: WebviewAppData) => state.externalUser.email,
    EXTERNAL_USER_PHONE: (state: WebviewAppData) => state.externalUser.phone,
    EXTERNAL_USER_OCCUPANT_TYPE_ID: (state: WebviewAppData) => state.externalUser.occupantTypeId,
    EXTERNAL_USER_API_KEY_USED: (state: WebviewAppData) => state.externalUser.apiKeyUsed,
    CURRENT_CHECK_IN: (state: WebviewAppData) => state.currentCheckIn,
    LOADING: (state: WebviewAppData) => state.loading,
  },
  mutations: {
    REMOVE_EXISTING_DATA: (state: WebviewAppData) => {
      state.locations = [];
      state.entrances = [];
      state.timeslots = [];
      state.occupantTypes = [];
      state.visitTypes = [];
      state.reservation = null;
      state.externalUser.reservations = [];
      state.externalUser.name = null;
      state.externalUser.email = null;
      state.externalUser.phone = null;
      state.externalUser.occupantTypeId = null;
      state.externalUser.apiKeyUsed = null;
      state.currentCheckIn.scan = null;
      state.currentCheckIn.location = null;
      state.currentCheckIn.reservation = null;
      state.currentCheckIn.externalUserId = null;
      state.currentCheckIn.autoCheckOutTimestamp = null;
    },
    SET_LOADING: (state: WebviewAppData, payload: boolean) => {
      state.loading = payload;
    },
    SET_QUERY_PARAMETERS: (state: WebviewAppData, payload: WebviewQueryStringParameters) => {
      state.queryParameters = payload;
    },
    SET_LOCATION: (state: WebviewAppData, payload: LocationEntity) => {
      const existingArrayIndex = state.locations.findIndex((location) => location.id === payload.id);

      if (existingArrayIndex !== -1) {
        state.locations[existingArrayIndex] = payload;
      } else {
        state.locations.push(payload);
      }
    },
    SET_LOCATIONS: (state: WebviewAppData, payload: LocationEntity[]) => {
      state.locations = payload;
    },
    SET_ENTRANCE: (state: WebviewAppData, payload: EntranceEntity) => {
      const existingArrayIndex = state.entrances.findIndex((entrance) => entrance.id === payload.id);

      if (existingArrayIndex !== -1) {
        state.entrances[existingArrayIndex] = payload;
      } else {
        state.entrances.push(payload);
      }
    },
    SET_ENTRANCES: (state: WebviewAppData, payload: EntranceEntity[]) => {
      state.entrances = payload;
    },
    SET_TIMESLOT: (state: WebviewAppData, payload: TimeslotEntity) => {
      const existingArrayIndex = state.entrances.findIndex((timeslot) => timeslot.id === payload.id);

      if (existingArrayIndex !== -1) {
        state.timeslots[existingArrayIndex] = payload;
      } else {
        state.timeslots.push(payload);
      }
    },
    SET_TIMESLOTS: (state: WebviewAppData, payload: TimeslotEntity[]) => {
      state.timeslots = payload;
    },
    SET_OCCUPANT_TYPE: (state: WebviewAppData, payload: OccupantTypeEntity) => {
      const existingArrayIndex = state.occupantTypes.findIndex((occupantType) => occupantType.id === payload.id);

      if (existingArrayIndex !== -1) {
        state.occupantTypes[existingArrayIndex] = payload;
      } else {
        state.occupantTypes.push(payload);
      }
    },
    SET_OCCUPANT_TYPES: (state: WebviewAppData, payload: OccupantTypeEntity[]) => {
      state.occupantTypes = payload;
    },
    SET_VISIT_TYPE: (state: WebviewAppData, payload: VisitTypeEntity) => {
      const existingArrayIndex = state.visitTypes.findIndex((visitType) => visitType.id === payload.id);

      if (existingArrayIndex !== -1) {
        state.visitTypes[existingArrayIndex] = payload;
      } else {
        state.visitTypes.push(payload);
      }
    },
    SET_VISIT_TYPES: (state: WebviewAppData, payload: VisitTypeEntity[]) => {
      state.visitTypes = payload;
    },
    SET_RESERVATION: (state: WebviewAppData, payload: ReservationEntity) => {
      state.reservation = payload;
    },
    SET_EXTERNAL_USER_RESERVATION: (state: WebviewAppData, payload: ReservationEntity) => {
      const existingArrayIndex = state.externalUser.reservations.findIndex((externalUserReservation) => externalUserReservation.id === payload.id);

      if (existingArrayIndex !== -1) {
        state.externalUser.reservations[existingArrayIndex] = payload;
      } else {
        state.externalUser.reservations.push(payload);
      }
    },
    SET_EXTERNAL_USER_RESERVATIONS: (state: WebviewAppData, payload: ReservationEntity[]) => {
      state.externalUser.reservations = payload;
    },
    SET_EXTERNAL_USER_NAME: (state: WebviewAppData, payload: string | null) => {
      state.externalUser.name = payload;
    },
    SET_EXTERNAL_USER_EMAIL: (state: WebviewAppData, payload: string | null) => {
      state.externalUser.email = payload;
    },
    SET_EXTERNAL_USER_PHONE: (state: WebviewAppData, payload: string | null) => {
      state.externalUser.phone = payload;
    },
    SET_EXTERNAL_USER_OCCUPANT_TYPE_ID: (state: WebviewAppData, payload: number | null) => {
      state.externalUser.occupantTypeId = payload;
    },
    SET_EXTERNAL_USER_API_KEY_USED: (state: WebviewAppData, payload: string | null) => {
      state.externalUser.apiKeyUsed = payload;
    },
    SET_CURRENT_CHECK_IN: (state: WebviewAppData, payload: CurrentCheckIn) => {
      state.currentCheckIn = { ...payload };
    },
  },
  actions: {
    GET_LOCATION: async ({ commit }: any, options: GetLocationOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/location/${options.locationId}?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        commit('SET_LOCATION', apiReturn);
      }
    },
    GET_ALL_LOCATIONS: async ({ commit }: any, options: GetAllEntitiesForCustomerOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/locations?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        apiReturn.forEach((value: LocationEntity, index: number) => {
          const disabledEmailPhoneToggle = (value.otherSettings as any).disabledEmailPhoneToggle;
          delete value.otherSettings;
          value.otherSettings = { disabledEmailPhoneToggle: disabledEmailPhoneToggle };

          if (index !== 0 && apiReturn.length > 3) {
            delete value.translations;
            delete value.visualSettings;
          }
        });

        commit('SET_LOCATIONS', apiReturn);
      }
    },
    GET_ENTRANCE: async ({ commit }: any, options: GetEntranceOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/location/${options.locationId}/entrance/${options.entranceId}?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        commit('SET_ENTRANCE', apiReturn);
      }
    },
    GET_ALL_ENTRANCES: async ({ commit }: any, options: GetAllEntitiesForLocationOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/location/${options.locationId}/entrances?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        commit('SET_ENTRANCES', apiReturn);
      }
    },
    GET_TIMESLOT: async ({ commit }: any, options: GetTimeslotOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/location/${options.locationId}/timeslot/${options.timeslotId}?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        commit('SET_TIMESLOT', apiReturn);
      }
    },
    GET_ALL_TIMESLOTS: async ({ commit }: any, options: GetAllEntitiesForLocationOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: options.visitTypeId
          ? `${apiBaseURL}customer/${options.customerId}/visitType/${options.visitTypeId}/timeslots?includeDateSummaries=true`
          : `${apiBaseURL}customer/${options.customerId}/location/${options.locationId}/timeslots?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        commit('SET_TIMESLOTS', apiReturn);
      }
    },
    GET_OCCUPANT_TYPE: async ({ commit }: any, options: GetOccupantTypeOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/occupantType/${options.occupantTypeId}?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        commit('SET_OCCUPANT_TYPE', apiReturn);
      }
    },
    GET_ALL_OCCUPANT_TYPES: async ({ commit }: any, options: GetAllEntitiesForCustomerOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/occupantTypes?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        commit('SET_OCCUPANT_TYPES', apiReturn);
      }
    },
    GET_VISIT_TYPE: async ({ commit }: any, options: GetVisitTypeOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/visitType/${options.visitTypeId}?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        commit('SET_VISIT_TYPE', apiReturn);
      }
    },
    GET_ALL_VISIT_TYPES: async ({ commit }: any, options: GetAllEntitiesForCustomerOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: options.apiKey || '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/visitTypes?includeDateSummaries=true`,
      });

      if (apiReturn !== null) {
        commit('SET_VISIT_TYPES', apiReturn);
      }
    },
    GET_EXTERNAL_USER_RESERVATIONS: async ({ commit }: any, options: GetExternalUserReservationsOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/reservations/${options.externalUserId}`,
      });

      if (apiReturn !== null) {
        const externalUserManualReservations = apiReturn
          .filter((externalUserReservation: ReservationEntity) => externalUserReservation.type === 2 && externalUserReservation.status === 1)
          .sort((a: ReservationEntity, b: ReservationEntity) => (a.manualGroup!.name < b.manualGroup!.name ? -1 : 1));

        externalUserManualReservations.forEach((value: ReservationEntity) => {
          value.locations.forEach((value, index) => {
            delete value.otherSettings;

            if (index !== 0) {
              delete value.translations;
              delete value.visualSettings;
            }
          });
        });

        const externalUserRegularReservations = apiReturn
          .filter(
            (externalUserReservation: ReservationEntity) =>
              externalUserReservation.type === 1 &&
              externalUserReservation.status === 1 &&
              moment().isSameOrBefore(moment(`${externalUserReservation.startDate!}T${externalUserReservation.timeslot!.endTime}`)),
          )
          .sort((a: ReservationEntity, b: ReservationEntity) => (moment(a.startDate!).isBefore(moment(b.startDate!)) ? -1 : 1));

        const sortedUserReservations = externalUserManualReservations.concat(externalUserRegularReservations);

        commit('SET_EXTERNAL_USER_RESERVATIONS', sortedUserReservations);

        commit('SET_EXTERNAL_USER_NAME', externalUserManualReservations[0] ? externalUserManualReservations[0].name : null);
        commit('SET_EXTERNAL_USER_EMAIL', externalUserManualReservations[0] ? externalUserManualReservations[0].email : null);
        commit('SET_EXTERNAL_USER_PHONE', externalUserManualReservations[0] ? externalUserManualReservations[0].phone : null);
        commit('SET_EXTERNAL_USER_OCCUPANT_TYPE_ID', externalUserManualReservations[0] ? externalUserManualReservations[0].occupantTypeId : null);
        commit('SET_EXTERNAL_USER_API_KEY_USED', externalUserManualReservations[0] ? externalUserManualReservations[0].apiKeyUsed : null);
      }
    },
    GET_RESERVATION: async ({ commit }: any, options: GetReservationOptions) => {
      const apiReturn = await apiGetRequest({
        apiKey: '',
        apiURL: `${apiBaseURL}customer/${options.customerId}/reservation/qrCode/${options.qrCode}`,
      });

      if (apiReturn !== null) {
        commit('SET_RESERVATION', apiReturn);
      }
    },
    GET_LOCATION_APP_DATA: async ({ dispatch, getters }: any, options: GetLocationAppDataOptions) => {
      try {
        const queryParameters: WebviewQueryStringParameters = getters.QUERY_PARAMETERS;

        const customerOptions = {
          apiKey: queryParameters.apiKey,
          customerId: queryParameters.customerId,
        };

        if (options.includeOccupantTypes) {
          await dispatch('GET_ALL_OCCUPANT_TYPES', customerOptions);
        }

        if (queryParameters.visitTypeId) {
          await dispatch('GET_VISIT_TYPE', { ...customerOptions, visitTypeId: queryParameters.visitTypeId });
        }

        const locationOptions = {
          ...customerOptions,
          locationId: options.locationId,
          ...(options.visitTypeId ? { visitTypeId: options.visitTypeId } : {}),
        };

        if (options.includeLocation) {
          await dispatch('GET_LOCATION', locationOptions);
        }

        await dispatch('GET_ALL_ENTRANCES', locationOptions);
        await dispatch('GET_ALL_TIMESLOTS', locationOptions);
      } catch (e) {
        console.log(e);

        logError(e);
      }
    },
    GET_CUSTOMER_APP_DATA: async ({ dispatch, getters }: any) => {
      try {
        const queryParameters: WebviewQueryStringParameters = getters.QUERY_PARAMETERS;

        const customerOptions = {
          apiKey: queryParameters.apiKey,
          customerId: queryParameters.customerId,
        };

        await dispatch('GET_ALL_LOCATIONS', customerOptions);
        await dispatch('GET_ALL_OCCUPANT_TYPES', customerOptions);

        if (queryParameters.visitTypeId) {
          await dispatch('GET_VISIT_TYPE', { ...customerOptions, visitTypeId: queryParameters.visitTypeId });
        }

        if (queryParameters.externalUserId !== null) {
          const externalUserReservationsOptions: GetExternalUserReservationsOptions = {
            customerId: queryParameters.customerId!,
            externalUserId: queryParameters.externalUserId,
          };

          await dispatch('GET_EXTERNAL_USER_RESERVATIONS', externalUserReservationsOptions);
        }
      } catch (e) {
        console.log(e);

        logError(e);
      }
    },
  },
};
